import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { s } from '../components/PeriodStatsPage.styles';
import { downloadAsExcel } from '../../../utils/downloadAsExcel';
import { useDragScroll } from '../../../utils/useDragScroll';

type RegionAuctionOverviewProps = {
  periodSummary: string;
  region: string;
  data?: RegionMetricsItem[];
  loading?: boolean;
  error?: string | null;
  usage: string;
  usageDetail?: string;
};

export type RegionMetricsItem = {
  id: string;
  usage: string;
  progress: number;
  wonCount: number;
  winRate: number;
  winPriceRate: number;
  avgBidders: number;
};

export default function RegionAuctionOverview({
  periodSummary,
  region,
  data = [],
  loading = false,
  error = null,
  usage,
  usageDetail,
}: RegionAuctionOverviewProps) {
  type SortKey =
    | 'progress'
    | 'wonCount'
    | 'winRate'
    | 'winPriceRate'
    | 'avgBidders';
  type SortDir = 'asc' | 'desc';

  const [sortKey, setSortKey] = useState<SortKey>('progress');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const rows = useMemo(() => {
    const cloned = [...data];
    cloned.sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      const diff = (va as number) - (vb as number);
      return sortDir === 'asc' ? diff : -diff;
    });
    return cloned;
  }, [data, sortKey, sortDir]);

  const avg = useMemo(() => {
    const n = data.length || 1;
    if (data.length === 0)
      return {
        progress: 0,
        wonCount: 0,
        winRate: 0,
        winPriceRate: 0,
        avgBidders: 0,
      };

    const sum = data.reduce(
      (a, c) => ({
        progress: a.progress + (c.progress ?? 0),
        wonCount: a.wonCount + (c.wonCount ?? 0),
        winRate: a.winRate + (c.winRate ?? 0),
        winPriceRate: a.winPriceRate + (c.winPriceRate ?? 0),
        avgBidders: a.avgBidders + (c.avgBidders ?? 0),
      }),
      { progress: 0, wonCount: 0, winRate: 0, winPriceRate: 0, avgBidders: 0 },
    );
    return {
      progress: Math.round(sum.progress / n),
      wonCount: Math.round(sum.wonCount / n),
      winRate: +(sum.winRate / n).toFixed(1),
      winPriceRate: +(sum.winPriceRate / n).toFixed(1),
      avgBidders: +(sum.avgBidders / n).toFixed(1),
    };
  }, [data]);

  const handleDownloadExcel = () => {
    const xlsxRows = rows.map((r) => ({
      지역: r.usage,
      진행건수: r.progress,
      낙찰건수: r.wonCount,
      '낙찰률(%)': r.winRate,
      '낙찰가율(%)': r.winPriceRate,
      '평균 응찰자 수': r.avgBidders,
    }));

    const avgRow = {
      지역: '평균',
      진행건수: avg.progress,
      낙찰건수: avg.wonCount,
      '낙찰률(%)': avg.winRate,
      '낙찰가율(%)': avg.winPriceRate,
      '평균 응찰자 수': avg.avgBidders,
    };

    downloadAsExcel([...xlsxRows, avgRow] as any[], '지역별_경매_지표');
  };

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#dcf7e8">
            <img src="/assets/images/icon/green_map.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>지역별 경매 지표 현황</h3>
          <p>전국 주요 지역별 경매 시장 현황 비교</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="지역별 경매 지표"
          />
        </LoaderWrap>
      ) : (
        <s.TableWrap>
          <TableContainer
            ref={ref}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
          >
            <Table>
              <thead>
                <tr>
                  <Th align="left">지역</Th>

                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('progress')}>
                      <Arrow $active={sortKey === 'progress'} $dir={sortDir} />
                      진행건수
                    </SortHeader>
                  </Th>

                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('wonCount')}>
                      <Arrow $active={sortKey === 'wonCount'} $dir={sortDir} />
                      낙찰건수
                    </SortHeader>
                  </Th>

                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('winRate')}>
                      <Arrow $active={sortKey === 'winRate'} $dir={sortDir} />
                      낙찰률
                    </SortHeader>
                  </Th>

                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('winPriceRate')}>
                      <Arrow
                        $active={sortKey === 'winPriceRate'}
                        $dir={sortDir}
                      />
                      낙찰가율
                    </SortHeader>
                  </Th>

                  <Th align="right">
                    <SortHeader onClick={() => toggleSort('avgBidders')}>
                      <Arrow
                        $active={sortKey === 'avgBidders'}
                        $dir={sortDir}
                      />
                      평균 응찰자 수
                    </SortHeader>
                  </Th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <Td align="left">{row.usage}</Td>
                    <Td align="right">
                      {row.progress.toLocaleString('ko-KR')}
                    </Td>
                    <Td align="right">
                      {row.wonCount.toLocaleString('ko-KR')}
                    </Td>
                    <Td align="right">{row.winRate.toFixed(1)}%</Td>
                    <Td align="right">{row.winPriceRate.toFixed(1)}%</Td>
                    <Td align="right">{row.avgBidders.toFixed(1)}</Td>
                  </tr>
                ))}

                <s.AvgTr>
                  <s.TdAvg align="left">평균</s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.progress.toLocaleString('ko-KR')}
                  </s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.wonCount.toLocaleString('ko-KR')}
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.winRate.toFixed(1)}%</s.TdAvg>
                  <s.TdAvg align="right">
                    {avg.winPriceRate.toFixed(1)}%
                  </s.TdAvg>
                  <s.TdAvg align="right">{avg.avgBidders.toFixed(1)}</s.TdAvg>
                </s.AvgTr>
              </tbody>
            </Table>
          </TableContainer>
        </s.TableWrap>
      )}
    </s.Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  gap: 24px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 40px;
`;

const Th = styled.th<{ align?: 'left' | 'center' | 'right' }>`
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  angle: 0 deg;
  opacity: 1;
  gap: 4px;
  padding: 12px 14px 12px 12px;

  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
`;

const Td = styled.td<{ align?: 'left' | 'center' | 'right' }>`
  padding: 14px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
`;

const SortHeader = styled.button`
  font-weight: 500;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
`;

const Arrow = styled.img.attrs<{ $active?: boolean; $dir?: 'asc' | 'desc' }>(
  ({ $active, $dir }) => {
    if ($active) {
      return {
        src:
          $dir === 'desc'
            ? '/assets/images/arrow/blue_arrow_down.svg' // 활성 + 오름
            : '/assets/images/arrow/red_arrow_up.svg', // 활성 + 내림
        alt: '',
      };
    }
    return { src: '/assets/images/arrow/gray_arrow_down.svg', alt: '' }; // 비활성
  },
)<{ $active?: boolean; $dir?: 'asc' | 'desc' }>`
  width: 16px;
  height: 16px;
  transition:
    transform 120ms ease,
    opacity 120ms ease;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;

const Table = styled.table`
  min-width: max(760px, 100%);
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;
  white-space: nowrap;

  thead th {
    background: #f4f5f7;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  thead th:first-child {
    border-top-left-radius: 8px;
  }

  thead th:last-child {
    border-top-right-radius: 8px;
  }

  tbody tr:not(:last-child) td {
    border-bottom: 1px solid #e8e9ed;
  }
`;
